import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../style.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, Button, Grid } from "@mui/material";
import { OutlinedInput, Stack, styled, Typography } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "../../../layouts/Page";
import NavbarMenu from "../../../components/common/NavbarMenu";
import { resetPassword } from "../../../apiservices/authentication";
import resetPasswordImg from "../../../assets/images/login/loginImg.png";

const schema = yup
  .object({
    password: yup
      .string()
      .min(8, "*Password must be at least 8 characters")
      .required("*Password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "*Passwords must match")
      .required("*Confirm Password is required"),
  })
  .required();

const FormBox = styled("div")({
  background: "white",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  minHeight: "450px",
  padding: "40px",
});

const RootStyle = styled("div")({
  overflowY: "hidden",
  background: "#04B2AB",
  minHeight: "88.5vh",
  display: "flex",
  alignItems: "center",
  padding: "0px 5%",
});

const TypoTitle = styled(Typography)({
  marginTop: 15,
  color: "#A7A7A7",
});

const TypoMain = styled(Typography)({
  marginTop: 15,
  fontWeight: "bold",
  color: "#21A49E",
});

const StyledButton = styled(Button)({
  height: "40px",
  width: "228px",
  borderRadius: "4px",
  background: "#21A49E",
  color: "#ffffff",
  textDecorationLine: "none",
  marginTop: "20px",
  "&:hover": {
    backgroundColor: "#1B8C87",
    color: "#ffffff",
  },
  "&:active": {
    backgroundColor: "#146F6A",
    color: "#ffffff",
  },
});

export const BackButton = styled(Button)({
  textTransform: "capitalize",
  color: "#21A49E !important",
  fontWeight: "500",
  fontSize: "15px",
  marginTop: "15px",
  "&:hover": {
    color: "#21A49E",
  },
  "&:active": {
    color: "#21A49E",
  },
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    showPassword: false,
    showConfirmPassword: false,
  });

  const resetToken = searchParams.get("token");
  const userId = searchParams.get("userId");

  useEffect(() => {
    if (!resetToken || !userId) {
      toast.error("Invalid reset link. Please request a new password reset.");
      setTimeout(() => {
        navigate("/forgot-password");
      }, 2000);
    }
  }, [resetToken, userId, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const resetData = {
        _id: userId,
        password: data.password,
        reset_password_token: resetToken,
      };

      const response = await resetPassword(resetData);

      if (response.data && response.data.userId) {
        toast.success("Password reset successfully!");
        setTimeout(() => {
          navigate("/sign-in");
        }, 2000);
      } else if (response.error) {
        toast.error(response.error);
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () =>
    setState((prev) => ({ ...prev, showPassword: !prev.showPassword }));

  const handleClickShowConfirmPassword = () =>
    setState((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));

  return (
    <PageLayout>
      <NavbarMenu />
      <RootStyle>
        <Grid container spacing={2} alignItems="center">
          <Grid item md={4} lg={6} display={{ xs: "none", md: "block" }}>
            <Stack alignItems="center" justifyContent="center">
              <img src={resetPasswordImg} alt="Reset Password" />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <FormBox sx={{ width: { xs: "350px", md: "500px" } }}>
              <TypoTitle>Reset Your Password</TypoTitle>
              <TypoMain variant="h6">Enter your new password</TypoMain>

              <Box sx={{ mt: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <FormControl
                    sx={{ m: 1, width: { sm: 300, md: 400 } }}
                    variant="outlined"
                  >
                    <InputLabel htmlFor="outlined-adornment-password">
                      New Password
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-password"
                      type={state.showPassword ? "text" : "password"}
                      {...register("password")}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {state.showPassword ? (
                              <Visibility color="disabled" fontSize="small" />
                            ) : (
                              <VisibilityOff
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="New Password"
                    />
                  </FormControl>
                  <Typography sx={{ justifyContent: "center", color: "red" }}>
                    {errors.password?.message}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl
                    sx={{ m: 1, width: { sm: 300, md: 400 } }}
                    variant="outlined"
                  >
                    <InputLabel htmlFor="outlined-adornment-confirm-password">
                      Confirm Password
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-confirm-password"
                      type={state.showConfirmPassword ? "text" : "password"}
                      {...register("confirmPassword")}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {state.showConfirmPassword ? (
                              <Visibility color="disabled" fontSize="small" />
                            ) : (
                              <VisibilityOff
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Confirm Password"
                    />
                  </FormControl>
                  <Typography sx={{ justifyContent: "center", color: "red" }}>
                    {errors.confirmPassword?.message}
                  </Typography>
                </Box>
              </Box>

              <StyledButton
                onClick={handleSubmit(onSubmit)}
                type="submit"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </StyledButton>

              <Box
                sx={{
                  borderTop: 1,
                  width: "300px",
                  marginTop: "30px",
                  color: "#D2D2D2",
                }}
              />

              <BackButton onClick={() => navigate("/sign-in")}>
                Back to Login
              </BackButton>
            </FormBox>
          </Grid>
        </Grid>
      </RootStyle>
    </PageLayout>
  );
};

export default ResetPassword;
